/**
 * Appointment Management list: closing & check-in column display + text colors.
 *
 * Palette (darker, heavier text — strong contrast on white backgrounds):
 * - Booking: forest green
 * - Not Interested: burgundy red
 * - Revisit: amber / yellow
 * - Follow-up: blue
 * - Checked In (revisit only): deep teal
 * - Upcoming Revisit / Revisit Scheduled: orange
 * - Empty → "-" via formatAppointmentCellValue (charcoal)
 */
import moment from "moment";

/** @type {Record<string, string>} */
export const APPOINTMENT_STATUS_COLORS = {
  /** Booked / Booking / Ready to book */
  booking: "#008000",
  notInterested: "#ff0000",
  /** Closing disposition "Revisit" (not "Upcoming Revisit" / not scheduled) */
  revisit: "#a16207",
  followup: "#1d4ed8",
  /** Check-in column "Checked In" (revisit) */
  visited: "#00cc00",
  upcomingRevisit: "#9a3412",
  reschedule: "#6d28d9",
  /** "-" and unknown / generic pending */
  neutral: "#3D4044",
  upcoming: "#2A3036",
  default: "#0D0F12",
};

/**
 * Show "-" when there is no meaningful text (after trim).
 * @param {unknown} value
 * @returns {string}
 */
export function formatAppointmentCellValue(value) {
  const t = String(value ?? "").trim();
  return t === "" ? "-" : t;
}

/**
 * Text color for Closing status cell from the label shown (raw, before formatAppointmentCellValue).
 * @param {unknown} label
 * @returns {string} CSS color
 */
export function getClosingStatusTextColor(label) {
  const raw = String(label ?? "").trim();
  if (!raw) return APPOINTMENT_STATUS_COLORS.neutral;

  const s = raw.toLowerCase().replace(/\s+/g, " ");

  if (
    s.includes("not interested") ||
    s.includes("not fit for sale") ||
    s === "not fit" ||
    s.includes("visit cancelled")
  ) {
    return APPOINTMENT_STATUS_COLORS.notInterested;
  }

  if (
    /\b(booked|booking)\b/i.test(raw) ||
    /ready\s+to\s+book/i.test(raw) ||
    /book\s+now/i.test(raw)
  ) {
    return APPOINTMENT_STATUS_COLORS.booking;
  }

  if (s.includes("upcoming revisit") || s.includes("revisit scheduled")) {
    return APPOINTMENT_STATUS_COLORS.upcomingRevisit;
  }

  if (
    s.includes("appointment rescheduled") ||
    (s.includes("reschedule") && !s.includes("checked"))
  ) {
    return APPOINTMENT_STATUS_COLORS.reschedule;
  }

  if (
    /\bfollow\b/.test(s) ||
    /\bcall\b/.test(s) ||
    /\bwhatsapp\b/.test(s) ||
    s.includes("next day call") ||
    s.includes("call/whatsapp") ||
    s.includes("call / whatsapp")
  ) {
    return APPOINTMENT_STATUS_COLORS.followup;
  }

  if (s.includes("revisit")) {
    return APPOINTMENT_STATUS_COLORS.revisit;
  }

  if (s === "not visited" || s === "notvisited") {
    return APPOINTMENT_STATUS_COLORS.upcoming;
  }

  if (
    /\bvisited\b/.test(s) ||
    s.includes("site visit done") ||
    s === "complete"
  ) {
    return APPOINTMENT_STATUS_COLORS.visited;
  }

  if (s === "not updated") {
    return APPOINTMENT_STATUS_COLORS.neutral;
  }

  if (s === "upcoming" || s.includes("reschedule")) {
    return APPOINTMENT_STATUS_COLORS.upcoming;
  }

  return APPOINTMENT_STATUS_COLORS.default;
}

/**
 * Check-in column: Checked In = teal; pending / "-" = neutral.
 * @param {boolean} showAsVisited
 * @returns {string} CSS color
 */
export function getCheckInStatusTextColor(showAsVisited) {
  return showAsVisited
    ? APPOINTMENT_STATUS_COLORS.visited
    : APPOINTMENT_STATUS_COLORS.neutral;
}

/**
 * Visitor list status column — same text-color pattern as appointment closing status.
 * @param {unknown} leadStatus
 * @param {unknown} label
 * @returns {string} CSS color
 */
/**
 * Shared status → badge class for Visitor, Appointment, Disposition.
 * Keep label matching order: checked-in / upcoming before generic revisit.
 */
export function getClosingStatusBadgeClass(label) {
  const raw = String(label ?? "").trim();
  if (!raw || raw === "-") return "visitor-status-badge--default";

  const s = raw.toLowerCase().replace(/\s+/g, " ");

  if (
    s.includes("not interested") ||
    s.includes("not fit for sale") ||
    s === "not fit" ||
    s.includes("visit cancelled") ||
    s.includes("cancel visit") ||
    s.includes("appointment cancel") ||
    (s.includes("cancel") && !s.includes("reschedule"))
  ) {
    return "visitor-status-badge--cancelled";
  }

  if (
    (s.includes("revisit") && (s.includes("check") || s.includes("checked"))) ||
    s === "checked in" ||
    s === "visited" ||
    s === "complete"
  ) {
    return "visitor-status-badge--checkedin";
  }

  if (
    s.includes("appointment rescheduled") ||
    (s.includes("reschedule") && !s.includes("checked"))
  ) {
    return "visitor-status-badge--reschedule";
  }

  if (
    s.includes("upcoming revisit") ||
    s.includes("revisit scheduled") ||
    s.includes("not checked in")
  ) {
    return "visitor-status-badge--ready";
  }

  if (
    /\b(booked|booking)\b/i.test(raw) ||
    /book\s+now/i.test(raw) ||
    s.includes("registration")
  ) {
    return "visitor-status-badge--booking";
  }

  if (
    s.includes("ready to visit") ||
    s.includes("ready to book") ||
    s === "ready"
  ) {
    return "visitor-status-badge--ready";
  }

  if (
    /\bfollow\b/.test(s) ||
    /\bcall\b/.test(s) ||
    /\bwhatsapp\b/.test(s) ||
    s.includes("next day call")
  ) {
    return "visitor-status-badge--followup";
  }

  if (s.includes("revisit")) {
    return "visitor-status-badge--revisit";
  }

  if (
    s.includes("new lead") ||
    s === "new" ||
    s === "upcoming"
  ) {
    return "visitor-status-badge--new";
  }

  if (s.includes("reschedule")) {
    return "visitor-status-badge--reschedule";
  }

  if (s === "unqualified") {
    return "visitor-status-badge--unqualified";
  }
  if (s === "qualified") {
    return "visitor-status-badge--qualified";
  }

  if (
    s.includes("not updated") ||
    s.includes("not visit") ||
    s === "n/a"
  ) {
    return "visitor-status-badge--default";
  }

  return "visitor-status-badge--default";
}

export function getCheckInStatusBadgeClass(showAsVisited) {
  return showAsVisited
    ? "visitor-status-badge--booking"
    : "visitor-status-badge--default";
}

export function getVisitorLeadStatusTextColor(leadStatus, label) {
  const status = Number(leadStatus);
  if (status === 1) return "#1d4ed8";
  if (status === 2) return APPOINTMENT_STATUS_COLORS.followup;
  if (status === 3 || status === 7) return "#6d28d9";
  if (status === 4 || status === 5) return APPOINTMENT_STATUS_COLORS.booking;
  if (status === 6) return APPOINTMENT_STATUS_COLORS.notInterested;
  return getClosingStatusTextColor(label);
}

const FOLLOWUP_CLOSING_ALIASES = new Set([
  "call",
  "whatsapp",
  "whats app",
  "call/whatsapp",
  "call / whatsapp",
  "next day call",
  "follow up",
  "follow-up",
  "followup",
  "follow up (call/whatsapp)",
]);

const toTimestamp = (value) => {
  if (!value) return 0;
  const ts = new Date(value).getTime();
  return Number.isNaN(ts) ? 0 : ts;
};

export function isCheckinStatusTitle(title = "") {
  return /check/i.test(String(title || "").trim());
}

export function hasCheckinRecord(checkinStatus) {
  if (checkinStatus === true) return true;
  if (!checkinStatus) return false;
  if (Array.isArray(checkinStatus)) {
    return checkinStatus.some(
      (entry) =>
        !!entry &&
        (entry === true ||
          entry?.status === true ||
          !!entry?.created_date ||
          !!entry?.createdDate ||
          !!entry?.checkin_date ||
          !!entry?.checkinDate ||
          !!entry?.updated_date ||
          !!entry?.updatedDate),
    );
  }
  if (typeof checkinStatus === "object") {
    return (
      checkinStatus?.status === true ||
      !!checkinStatus?.created_date ||
      !!checkinStatus?.createdDate ||
      !!checkinStatus?.checkin_date ||
      !!checkinStatus?.checkinDate ||
      !!checkinStatus?.updated_date ||
      !!checkinStatus?.updatedDate
    );
  }
  return false;
}

export function getLatestCheckinTs(checkinStatus) {
  if (!checkinStatus) return 0;
  if (Array.isArray(checkinStatus)) {
    return checkinStatus.reduce((best, entry) => {
      if (!entry) return best;
      return Math.max(
        best,
        toTimestamp(entry?.created_date),
        toTimestamp(entry?.createdDate),
        toTimestamp(entry?.checkin_date),
        toTimestamp(entry?.checkinDate),
        toTimestamp(entry?.updated_date),
        toTimestamp(entry?.updatedDate),
      );
    }, 0);
  }
  if (typeof checkinStatus === "object") {
    return Math.max(
      toTimestamp(checkinStatus?.created_date),
      toTimestamp(checkinStatus?.createdDate),
      toTimestamp(checkinStatus?.checkin_date),
      toTimestamp(checkinStatus?.checkinDate),
      toTimestamp(checkinStatus?.updated_date),
      toTimestamp(checkinStatus?.updatedDate),
    );
  }
  return checkinStatus === true ? Date.now() : 0;
}

export function isSiteVisitMappedVisited(siteVisitStatus) {
  return (
    siteVisitStatus === "Visited" ||
    siteVisitStatus === "Completed" ||
    siteVisitStatus === "Site Visit Done"
  );
}

export function isPendingRevisitCheckin(item = {}) {
  if (
    item?.revisit_pending_checkin === true ||
    item?.lead_revisit_pending_checkin === true
  ) {
    return true;
  }
  if (item?.revisit_pending_checkin === false) return false;
  const lastTitle = String(
    item?.last_followup_title || item?.lastFollowupTitle || "",
  )
    .trim()
    .toLowerCase();
  if (lastTitle !== "revisit") return false;
  const checkinTs = Math.max(
    toTimestamp(item?.checkin_date),
    getLatestCheckinTs(item?.checkin_status),
  );
  const revisitTs = toTimestamp(item?.last_followup_created_date);
  if (!checkinTs || !revisitTs) return true;
  return revisitTs >= checkinTs;
}

export function normalizeFollowupClosingStatus(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (!normalized) return "";
  if (FOLLOWUP_CLOSING_ALIASES.has(normalized)) return "Follow-Up";
  return String(value || "").trim();
}

/**
 * Check-in column — "Checked In" when a check-in exists.
 * Pending revisit (scheduled, not yet checked in) shows "Not Checked In".
 * Closing status stays in its own column.
 */
export function isRevisitCheckInContext(item = {}) {
  if (isPendingRevisitCheckin(item)) return true;
  const status = Number(item?.status);
  if (status === 2) return true;
  const type = Number(item?.type ?? item?.appointment_type);
  if (type === 2) return true;
  const last = String(
    item?.last_followup_title || item?.lastFollowupTitle || "",
  )
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (last.includes("revisit")) return true;
  const closing = String(
    item?.effective_closing_status || item?.closing_status || "",
  )
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (closing.includes("revisit")) return true;
  return false;
}

export function isFollowupClosingLike(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (!normalized) return false;
  if (FOLLOWUP_CLOSING_ALIASES.has(normalized)) return true;
  if (
    normalized.includes("follow-up") ||
    normalized.includes("follow up") ||
    normalized.includes("followup")
  ) {
    return true;
  }
  // Call / WhatsApp follow-up titles (exclude visit cancel / appointment cancel)
  if (/\b(call|whatsapp)\b/.test(normalized) && !/\bcancel/.test(normalized)) {
    return true;
  }
  return false;
}

export function resolveCheckInDisplay(item = {}) {
  const lastFollowupTitle =
    item?.last_followup_title || item?.lastFollowupTitle || "";
  const hasCheckin =
    hasCheckinRecord(item?.checkin_status) ||
    !!item?.checkin_date ||
    (isCheckinStatusTitle(lastFollowupTitle) &&
      item?.revisit_pending_checkin !== true);
  const mappedVisited = isSiteVisitMappedVisited(item?.site_visit_status);
  const pendingRevisit = isPendingRevisitCheckin(item);
  const checkedIn = (hasCheckin || mappedVisited) && !pendingRevisit;

  let label = "";
  let showAsVisited = false;

  if (pendingRevisit) {
    label = "Not Checked In";
    showAsVisited = false;
  } else if (checkedIn) {
    label = "Checked In";
    showAsVisited = true;
  }

  return {
    showAsVisited,
    label,
    hasCheckin: hasCheckin || mappedVisited,
    pendingRevisit,
  };
}

/**
 * Closing / visited status — prefer backend effective_closing_status (latest
 * follow-up / check-in), then infer from the same signals the app uses.
 */
export function resolveClosingStatusLabel(item = {}) {
  const checkIn = resolveCheckInDisplay(item);
  const effective = String(item?.effective_closing_status || "").trim();
  if (effective) {
    return normalizeFollowupClosingStatus(effective);
  }

  let lastTitle = String(
    item?.last_followup_title || item?.lastFollowupTitle || "",
  ).trim();
  if (lastTitle.toLowerCase() === "lead create") {
    lastTitle = "";
  }
  const closingRaw = String(item?.closing_status || "").trim();
  const closingTitle = closingRaw || lastTitle;
  const status = Number(item?.status);

  if (item?.revisit_superseded === true && !checkIn.hasCheckin) {
    return "Revisit Scheduled";
  }
  if (checkIn.pendingRevisit && !checkIn.showAsVisited) {
    return "Upcoming Revisit";
  }

  const normalizedClosing = closingTitle.toLowerCase().replace(/\s+/g, " ");
  const isFollowupLike = isFollowupClosingLike(closingTitle);
  const postCheckin = checkIn.hasCheckin && !checkIn.pendingRevisit;
  if (postCheckin && isFollowupLike) {
    return "Follow-Up";
  }
  if (
    postCheckin &&
    (!closingTitle || normalizedClosing === "not updated")
  ) {
    return "Not Updated";
  }
  if (closingTitle) {
    return normalizeFollowupClosingStatus(closingTitle);
  }

  if (status === 1) {
    const appointmentMoment = moment(
      `${moment(item.appointment_date).utc().format("DD-MM-yyyy")} ${
        item.appointment_time || ""
      }`.trim(),
      "DD-MM-yyyy hh:mm A",
    );
    if (appointmentMoment.isValid() && moment() >= appointmentMoment) {
      return "Not Visited";
    }
    return "Upcoming";
  }
  if (status === 2) {
    return postCheckin ? "Not Updated" : "Upcoming Revisit";
  }
  if (status === 3) return "Booking";
  if (status === 4) return "Visit Cancelled";
  if (status === 5) {
    return postCheckin ? "Reschedule - Checked in" : "Reschedule";
  }
  if (status === 6) return "Not Fit for Sale";
  if (status === 10) {
    return postCheckin ? "Not Updated" : "Follow-Up";
  }
  return "";
}

/**
 * Appointment detail CTAs — same modes as the app (JUSTO-107):
 * pre_checkin → No Show + Visitor Check-in
 * post_checkin → Not Interested / Revisit / Follow-up / Book Now
 */
export function resolveAppointmentActionButtons(item = {}) {
  const status = Number(item?.status);
  const isRevisitScheduledFinal = item?.revisit_superseded === true;
  const checkIn = resolveCheckInDisplay(item);
  const mode = String(item?.effective_action_mode || "")
    .trim()
    .toLowerCase();

  if (status === 3 || status === 4 || status === 6 || isRevisitScheduledFinal) {
    return { showNoShowCheckin: false, showPostCheckin: false };
  }
  if (mode === "final") {
    return { showNoShowCheckin: false, showPostCheckin: false };
  }
  if (mode === "pre_checkin") {
    return { showNoShowCheckin: true, showPostCheckin: false };
  }
  if (mode === "post_checkin") {
    return { showNoShowCheckin: false, showPostCheckin: true };
  }

  const isUpcomingRevisit = checkIn.pendingRevisit && !checkIn.showAsVisited;
  if (isUpcomingRevisit) {
    return { showNoShowCheckin: true, showPostCheckin: false };
  }
  if (checkIn.hasCheckin && !checkIn.pendingRevisit) {
    return { showNoShowCheckin: false, showPostCheckin: true };
  }
  if (status === 1 || status === 2 || status === 5 || status === 10) {
    return { showNoShowCheckin: true, showPostCheckin: false };
  }
  return { showNoShowCheckin: false, showPostCheckin: false };
}

/**
 * Book Now after check-in — same hide rule as the app:
 * hide when closing is Follow-up, unless closing is still Not Updated.
 */
export function shouldShowPostCheckinBookNow(item = {}) {
  const closing = String(resolveClosingStatusLabel(item) || "")
    .toLowerCase()
    .replace(/\s+/g, " ");
  const effective = String(item?.effective_closing_status || "")
    .trim()
    .toLowerCase();
  const lastTitle = String(
    item?.last_followup_title || item?.lastFollowupTitle || "",
  )
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
  const isFollowUpClosing =
    effective === "follow-up" ||
    closing === "follow-up" ||
    FOLLOWUP_CLOSING_ALIASES.has(lastTitle);
  const isNotUpdated = closing === "not updated";
  return !isFollowUpClosing || isNotUpdated;
}

/**
 * Same gate as the apps: after check-in, leaving details without a real
 * closing status would skip disposition — warn before navigating away.
 */
export function appointmentHasCheckinRecord(item = {}) {
  if (item?.checkin_date) return true;
  return hasCheckinRecord(item?.checkin_status);
}

export function resolveAppointmentFinalStatus(item = {}) {
  const rawBookingStatus = Array.isArray(item?.booking_status)
    ? item?.booking_status?.[0]
    : item?.booking_status;
  const bookingStatusCode = Number(rawBookingStatus);
  const leadStatusCode = Number(item?.lead_status);

  if (
    bookingStatusCode === 2 ||
    bookingStatusCode === 3 ||
    leadStatusCode === 4
  ) {
    return { label: "Booked" };
  }
  if (bookingStatusCode === 4) {
    return { label: "Cancel Booking" };
  }
  return null;
}

export function shouldWarnClosingStatusOnBack(item = {}) {
  if (!appointmentHasCheckinRecord(item)) return false;
  if (resolveAppointmentFinalStatus(item)) return false;

  const apptStatus = Number(item?.status);
  if (apptStatus === 3 || apptStatus === 4 || apptStatus === 6) return false;

  const closing = String(
    item?.effective_closing_status ?? item?.closing_status ?? "",
  )
    .trim()
    .toLowerCase();
  const compact = closing.replace(/[\s_-]/g, "");
  if (closing && closing !== "not updated" && compact !== "notupdated") {
    return false;
  }
  return true;
}
